import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TERMS_VERSION } from "@/lib/legal/terms";
import { toast } from "sonner";
import { ShieldCheck, Clock, Lock } from "lucide-react";

type State =
  | { kind: "loading" }
  | { kind: "ok" }
  | { kind: "terms" }
  | { kind: "suspended" }
  | { kind: "expired"; until: string | null };

interface AccountStateRow {
  status: string;
  beta_expires_at: string | null;
  expired: boolean;
}

/**
 * Spärr som körs innanför inloggningen:
 *  1. Kontot måste vara aktivt (inte pausat eller utgången betaperiod).
 *  2. Aktuell version av användarvillkoren måste vara godkänd.
 */
export const AccountGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const evaluate = useCallback(async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return setState({ kind: "ok" });

      const { data: accountRows } = await supabase.rpc("get_account_state");
      const account = (accountRows as unknown as AccountStateRow[] | null)?.[0];

      if (account?.status === "suspended") return setState({ kind: "suspended" });
      if (account?.expired) {
        return setState({ kind: "expired", until: account.beta_expires_at ?? null });
      }

      const { data: agreement } = await supabase
        .from("user_agreements")
        .select("id")
        .eq("user_id", userId)
        .eq("agreement_version", TERMS_VERSION)
        .maybeSingle();

      setState(agreement ? { kind: "ok" } : { kind: "terms" });
    } catch {
      // Nätverksfel ska inte låsa ute användaren
      setState({ kind: "ok" });
    }
  }, []);

  useEffect(() => {
    void evaluate();
  }, [evaluate]);

  const accept = async () => {
    setSaving(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return;
      const { error } = await supabase.from("user_agreements").insert({
        user_id: userId,
        agreement_version: TERMS_VERSION,
        user_agent: navigator.userAgent.slice(0, 300),
      });
      if (error) throw error;
      setState({ kind: "ok" });
    } catch {
      toast.error("Kunde inte spara godkännandet. Försök igen.");
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
      </div>
    );
  }

  if (state.kind === "ok") return <>{children}</>;

  if (state.kind === "suspended" || state.kind === "expired") {
    const expired = state.kind === "expired";
    return (
      <div className="container mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 text-center">
        <div className="mb-4 rounded-full bg-white/10 p-4">
          {expired ? (
            <Clock className="h-7 w-7 text-on-hero" />
          ) : (
            <Lock className="h-7 w-7 text-on-hero" />
          )}
        </div>
        <h1 className="mb-2 text-2xl font-semibold text-on-hero">
          {expired ? "Din betaperiod har gått ut" : "Kontot är pausat"}
        </h1>
        <p className="text-sm text-on-hero/80">
          {expired
            ? "Kontakta oss så förlänger vi åtkomsten. Dina projekt ligger kvar orörda."
            : "Kontakta oss för att aktivera kontot igen."}
        </p>
        {expired && state.until && (
          <p className="mt-2 text-xs text-on-hero/60">
            Perioden avslutades {new Date(state.until).toLocaleDateString("sv-SE")}.
          </p>
        )}
        <Button variant="outline" className="mt-6" onClick={() => void supabase.auth.signOut()}>
          Logga ut
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto flex min-h-[70vh] max-w-xl flex-col justify-center px-4 py-10">
      <div className="mb-4 flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-on-hero" />
        <h1 className="text-2xl font-semibold text-on-hero">Godkänn villkoren</h1>
      </div>
      <p className="mb-4 text-sm leading-relaxed text-on-hero/80">
        Innan du börjar behöver du godkänna användarvillkoren. Kort sammanfattat: kontot är
        personligt, mallar och frågeunderlag får inte kopieras eller säljas vidare, och funktioner
        som ännu inte är publicerade omfattas av sekretess. Dina egna projektuppgifter tillhör dig.
      </p>
      <ul className="mb-5 space-y-1 text-sm text-on-hero/70">
        <li>· Endast för egna projekt – ingen vidareförsäljning.</li>
        <li>· Inga kopior av mallar, frågebibliotek eller processtruktur.</li>
        <li>· Exporterade dokument märks med en unik verifieringskod.</li>
      </ul>

      <label className="mb-5 flex items-start gap-3 text-sm text-on-hero">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => setChecked(value === true)}
          className="mt-0.5"
        />
        <span>
          Jag har läst och godkänner{" "}
          <Link to="/villkor" target="_blank" className="underline">
            användarvillkoren
          </Link>{" "}
          och{" "}
          <Link to="/integritetspolicy" target="_blank" className="underline">
            integritetspolicyn
          </Link>
          .
        </span>
      </label>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button disabled={!checked || saving} onClick={() => void accept()}>
          {saving ? "Sparar…" : "Godkänn och fortsätt"}
        </Button>
        <Button variant="outline" onClick={() => void supabase.auth.signOut()}>
          Logga ut
        </Button>
      </div>
      <p className="mt-4 text-xs text-on-hero/50">Version {TERMS_VERSION}</p>
    </div>
  );
};

export default AccountGate;
